/**
 * MapScene — Full-screen Leaflet map animation for the scan's first two stages:
 *
 *  1. "Locating Business"    → zooms in on the business pin with an optional logo badge
 *  2. "Discovering Competitors" → flies to each competitor pin sequentially
 *     (zoom-in → name-popup dwell → zoom-out → next), then shows an overview of all pins
 *
 * `onAnimationComplete` is called when the full cycle finishes so that ScanPage
 * can transition to the next scene regardless of how fast the backend progresses.
 */
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { AttributionControl, MapContainer, TileLayer, useMap } from 'react-leaflet'
import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { BRAND, CARTO_ATTRIBUTION, CARTO_TILE_URL } from '../../config'
import type { CompetitorResult, ScanStageKey } from '../../types'

// ── Animation timing constants ────────────────────────────────────────────────

/** Delay before the business zoom-in fly starts (gives tiles time to render). */
const PHASE1_FLY_DELAY    = 400
/** Duration of the business flyTo in milliseconds (matches flyTo duration: 2.2s). */
const PHASE1_FLY_DURATION = 2200
/** Dwell on the business after zoom-in before starting the competitor tour. */
const PHASE1_DWELL        = 800
/** When the competitor tour should begin (ms from animation start). */
const TOUR_START = PHASE1_FLY_DELAY + PHASE1_FLY_DURATION + PHASE1_DWELL // 3400ms
/** Total time budget per competitor: fly-in + dwell with popup. */
const PER_COMP          = 2600
/** Fly-in duration (seconds) passed to Leaflet flyTo. */
const COMP_FLY_SECS     = 1.5
/** Delay after a competitor timer fires before opening its popup. */
const COMP_POPUP_DELAY  = 1600
/** Maximum number of competitors to fly-to individually during the tour. */
const MAX_TOUR_COMPS    = 5
/** Gap between last competitor and overview zoom-out. */
const ZOOM_OUT_DELAY    = 700
/**
 * Safety fallback: if the competitor data never arrives (stage skipped / no GPS),
 * signal done after this many ms so the page is not stuck on the map forever.
 */
const FALLBACK_DONE_MS = TOUR_START + 3000  // ~6.4s

// ── Icon factories ────────────────────────────────────────────────────────────

/**
 * Creates the business location marker.
 * If `logoUrl` is provided a circular logo thumbnail is rendered above the badge.
 */
function makeBusinessIcon(name: string, logoUrl?: string | null) {
  const label = (name || 'Your Business').slice(0, 22)

  const logoHtml = logoUrl
    ? `<img
        src="${logoUrl}"
        alt="logo"
        style="
          width:30px;height:30px;border-radius:50%;object-fit:cover;
          border:2px solid #fff;margin-bottom:4px;
          box-shadow:0 2px 8px rgba(0,0,0,0.25);display:block;
        "
        onerror="this.style.display='none'"
      />`
    : ''

  const pinHeight = logoUrl ? 80 : 42

  return L.divIcon({
    html: `
      <div style="display:inline-flex;flex-direction:column;align-items:center;">
        ${logoHtml}
        <div style="
          background:#01114f;color:#fff;padding:5px 11px;
          border-radius:20px;font-size:12px;font-weight:700;
          white-space:nowrap;box-shadow:0 4px 14px rgba(1,17,79,0.5);
          border:2.5px solid #fff;
        ">📍 ${label}</div>
        <div style="
          width:0;height:0;
          border-left:6px solid transparent;border-right:6px solid transparent;
          border-top:8px solid #01114f;margin-top:-1px;
        "></div>
      </div>`,
    className: 'leaflet-custom-icon',
    iconSize:   [Math.min(label.length * 8 + 50, 200), pinHeight],
    iconAnchor: [Math.min(label.length * 4 + 25, 100), pinHeight],
  })
}

/** Creates a numbered red circle marker for each competitor. */
function makeCompetitorIcon(index: number) {
  return L.divIcon({
    html: `
      <div style="
        background:#ef042d;color:#fff;
        width:32px;height:32px;border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-size:13px;font-weight:700;
        border:2.5px solid #fff;
        box-shadow:0 3px 10px rgba(239,4,45,0.45);
      ">${index}</div>`,
    className: 'leaflet-custom-icon',
    iconSize:   [32, 32],
    iconAnchor: [16, 16],
  })
}

// ── MapAnimator: imperative Leaflet camera controller ────────────────────────

interface AnimatorProps {
  businessLat: number
  businessLng: number
  businessName: string
  businessLogoUrl?: string | null
  competitors: CompetitorResult[] | null
  onAnimationComplete: () => void
  onVisitingCompetitor: (index: number | null, total: number) => void
}

function MapAnimator({
  businessLat,
  businessLng,
  businessName,
  businessLogoUrl,
  competitors,
  onAnimationComplete,
  onVisitingCompetitor,
}: AnimatorProps) {
  const map = useMap()

  // Stable ref so timers always call the latest callback without going stale.
  const onDoneRef = useRef(onAnimationComplete)
  useEffect(() => { onDoneRef.current = onAnimationComplete }, [onAnimationComplete])

  const onVisitRef = useRef(onVisitingCompetitor)
  useEffect(() => { onVisitRef.current = onVisitingCompetitor }, [onVisitingCompetitor])

  // Guards to prevent running phases more than once.
  const animatedFor = useRef<CompetitorResult[] | null>(null)
  const phase1Done  = useRef(false)
  const doneCalled  = useRef(false)

  const callDone = () => {
    if (doneCalled.current) return
    doneCalled.current = true
    onVisitRef.current(null, 0)
    onDoneRef.current()
  }

  // ── Phase 1: place business pin + start zoom-in + safety fallback ──────────
  useEffect(() => {
    if (phase1Done.current) return
    phase1Done.current = true

    // Drop the business marker immediately
    const icon = makeBusinessIcon(businessName, businessLogoUrl)
    L.marker([businessLat, businessLng], { icon, zIndexOffset: 1000 }).addTo(map)

    // Start the zoom-in after a short tile-load delay
    const flyTimer = setTimeout(() => {
      map.flyTo([businessLat, businessLng], 16, {
        duration: PHASE1_FLY_DURATION / 1000,
        easeLinearity: 0.4,
      })
    }, PHASE1_FLY_DELAY)

    // Safety fallback: if the competitor dataset NEVER arrives (stage skipped /
    // no GPS coordinates), mark the animation done after a reasonable timeout.
    // IMPORTANT: only fire when Phase 2 has NOT started — if animatedFor is
    // already set it means competitors arrived and their own done-timer is
    // running; firing the fallback here would cut the tour short.
    const fallbackTimer = setTimeout(() => {
      if (animatedFor.current === null) {
        callDone()
      }
    }, FALLBACK_DONE_MS)

    return () => {
      clearTimeout(flyTimer)
      clearTimeout(fallbackTimer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // intentionally empty — Phase 1 runs exactly once on mount

  // ── Phase 2+3: competitor tour → overview zoom-out → signal done ────────────
  useEffect(() => {
    // Wait until the competitor data arrives from the SSE stream
    if (competitors === null) return

    // Guard against duplicate animation if React re-renders with the same data
    if (animatedFor.current === competitors) return
    animatedFor.current = competitors

    const timers: ReturnType<typeof setTimeout>[] = []
    const validComps = competitors.filter(c => c.latitude && c.longitude)

    // ── Empty competitor list ─────────────────────────────────────────────────
    // No places to tour — hold on the business zoom-in then mark done.
    if (validComps.length === 0) {
      const t = setTimeout(() => callDone(), TOUR_START + 1500)
      timers.push(t)
      return () => timers.forEach(clearTimeout)
    }

    // Limit the animated fly-to tour to MAX_TOUR_COMPS; any additional
    // competitors still get pins but are shown silently during the zoom-out.
    const tourComps = validComps.slice(0, MAX_TOUR_COMPS)

    // ── Competitor tour ───────────────────────────────────────────────────────
    tourComps.forEach((comp, i) => {
      const t = setTimeout(() => {
        // Notify the overlay so it can update its label
        onVisitRef.current(i + 1, tourComps.length)

        // Drop the numbered pin
        const icon   = makeCompetitorIcon(i + 1)
        const marker = L.marker([comp.latitude, comp.longitude], { icon })
          .bindPopup(
            `<div style="text-align:center;padding:4px 6px;min-width:120px">
              <strong style="font-size:12px;color:#111;display:block;margin-bottom:3px">
                ${comp.name}
              </strong>
              ${comp.rating > 0
                ? `<span style="color:#f59e0b;font-size:11px;">★ ${comp.rating.toFixed(1)}</span>`
                : ''}
              ${comp.address
                ? `<div style="color:#888;font-size:10px;margin-top:3px">
                    ${comp.address.length > 40 ? comp.address.slice(0, 40) + '…' : comp.address}
                   </div>`
                : ''}
            </div>`,
            { closeButton: false, autoPan: false, offset: [0, -8] },
          )
          .addTo(map)

        // Fly in to the competitor location
        map.flyTo([comp.latitude, comp.longitude], 16, {
          duration: COMP_FLY_SECS,
          easeLinearity: 0.4,
        })

        // Open the name popup after the fly-in so the user can read it during dwell
        const popupT = setTimeout(() => marker.openPopup(), COMP_POPUP_DELAY)
        timers.push(popupT)
      }, TOUR_START + i * PER_COMP)
      timers.push(t)
    })

    // ── Phase 3: overview zoom-out ────────────────────────────────────────────
    // Timing is based on tourComps.length (the capped set we actually flew to).
    const ZOOM_OUT_AT = TOUR_START + tourComps.length * PER_COMP + ZOOM_OUT_DELAY
    const zoomOutT = setTimeout(() => {
      onVisitRef.current(null, tourComps.length) // clear "visiting #N" label

      // Silently drop pins for competitors beyond the tour limit so they appear
      // in the overview map without getting their own fly-to animation.
      validComps.slice(MAX_TOUR_COMPS).forEach((comp, j) => {
        const icon = makeCompetitorIcon(MAX_TOUR_COMPS + j + 1)
        L.marker([comp.latitude, comp.longitude], { icon }).addTo(map)
      })

      // Fit bounds around ALL competitors (toured + silent) + the business.
      const allPoints: L.LatLngTuple[] = [
        [businessLat, businessLng],
        ...validComps.map(c => [c.latitude, c.longitude] as L.LatLngTuple),
      ]
      map.flyToBounds(L.latLngBounds(allPoints), {
        padding:  [60, 60],
        maxZoom:  14,
        animate:  true,
      })
    }, ZOOM_OUT_AT)
    timers.push(zoomOutT)

    // ── Phase 4: signal animation complete after overview dwell ───────────────
    // Leaflet's flyToBounds animation typically takes 1–2s. We wait 2.5s as a
    // safe buffer to ensure the overview has settled before transitioning away.
    const DONE_AT = ZOOM_OUT_AT + 2500
    const doneT = setTimeout(() => callDone(), DONE_AT)
    timers.push(doneT)

    return () => timers.forEach(clearTimeout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitors]) // only re-run when the competitor dataset identity changes

  return null
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function MapLoadingSkeleton() {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: BRAND.navy }} />
      <p className="text-sm text-gray-500 font-medium">Preparing map…</p>
    </div>
  )
}

// ── Public component ──────────────────────────────────────────────────────────

interface MapSceneProps {
  businessLat: number | null
  businessLng: number | null
  businessName: string | null
  businessLogoUrl?: string | null
  competitors: CompetitorResult[] | null
  activeKey: ScanStageKey | undefined
  message: string
  onAnimationComplete: () => void
}

export function MapScene({
  businessLat,
  businessLng,
  businessName,
  businessLogoUrl,
  competitors,
  activeKey,
  message,
  onAnimationComplete,
}: MapSceneProps) {
  /**
   * Local UI state tracking which competitor is currently being visited.
   * Driven by callbacks from MapAnimator so the overlay label stays in sync
   * with the camera animation without any shared mutable state.
   */
  const [visitState, setVisitState] = useState<{ index: number | null; total: number }>({
    index: null,
    total: 0,
  })

  if (!businessLat || !businessLng) {
    return <MapLoadingSkeleton />
  }

  const stageLabel =
    activeKey === 'resolve_target_business' ? 'Locating Business'       :
    activeKey === 'discover_competitors'    ? 'Discovering Competitors' :
    activeKey === 'check_rankings'          ? 'Checking Rankings'       : ''

  // Derive the dynamic status message shown in the overlay bar.
  // While touring competitors, show "Competitor N of M" so the user knows where they are.
  const competitorCount = competitors?.length ?? 0
  const overlayMessage =
    visitState.index !== null && visitState.total > 0
      ? `Competitor ${visitState.index} of ${visitState.total}`
      : message || 'Scanning…'

  return (
    <div className="relative w-full h-full">
      {/*
        Leaflet map — user interaction is disabled so the programmatic camera
        animations play cleanly without interference.
      */}
      <MapContainer
        center={[businessLat, businessLng]}
        zoom={12}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        keyboard={false}
      >
        <TileLayer
          url={CARTO_TILE_URL}
          attribution={CARTO_ATTRIBUTION}
        />
        {/* Top-right: the bottom of the scene is covered by the progress bar. */}
        <AttributionControl position="topright" />
        <MapAnimator
          businessLat={businessLat}
          businessLng={businessLng}
          businessName={businessName ?? 'Your Business'}
          businessLogoUrl={businessLogoUrl}
          competitors={competitors}
          onAnimationComplete={onAnimationComplete}
          onVisitingCompetitor={(index, total) => setVisitState({ index, total })}
        />
      </MapContainer>

      {/* Status overlay bar */}
      <div className="absolute bottom-0 left-0 right-0 z-[500] pointer-events-none">
        <div className="map-status-bar mx-4 mb-4 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-lg border border-gray-100/60">

          {/* Stage badge */}
          <AnimatePresence mode="wait">
            {stageLabel && (
              <motion.span
                key={stageLabel}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0"
                style={{ background: BRAND.navy, color: '#fff' }}
              >
                {stageLabel}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Live message / competitor progress */}
          <AnimatePresence mode="wait">
            <motion.p
              key={overlayMessage}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: 0.25 }}
              className="text-sm font-medium text-gray-700 flex-1 truncate"
            >
              {overlayMessage}
            </motion.p>
          </AnimatePresence>

          {/* Competitor count badge — visible once we have competitors */}
          {competitorCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
              style={{ background: BRAND.red, color: '#fff' }}
            >
              {visitState.index !== null
                ? `#${visitState.index} / ${competitorCount}`
                : `${competitorCount} nearby`}
            </motion.span>
          )}

          {/* Pulsing dots */}
          <div className="flex gap-1 shrink-0">
            {[0, 1, 2].map(i => (
              <motion.span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: BRAND.red }}
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                transition={{ repeat: Infinity, delay: i * 0.2, duration: 0.8 }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
